import { useState } from "react";
import * as Icons from "lucide-react";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Controller, useFormContext, UseFormReturn } from "react-hook-form";
import { MatchFilterState } from "../(context)/filter";
import { useFormReset } from "@/hooks/use-form-reset";
import SelectCompetition from "@/components/features/competitions/select-competition";
import SelectClub from "@/components/features/clubs/select-club";
import { DateInput } from "@/components/ui/date-input";
import { convertToDate } from "@/lib/date";
import { useTRPC } from "@/trpc";
import { useAuth } from "@/hooks/use-auth";
import { useCreateFilter } from "@/hooks/use-create-filter"
import { Dialog, DialogHeader, DialogTitle, DialogContent, DialogFooter } from "@/components/ui/dialog";
import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner"


type Props = {
  onSearch?: () => void;
  mode?: "create" | "edit";                    
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onUpdate?: (updated: { values: MatchFilterState; name?: string }) => void;
  initialFilterName?: string | null | undefined; 
}

export const NewSearchFilter = ({
  onSearch,
  mode = "create",
  open: controlledOpen,
  onOpenChange,
  onUpdate,
  initialFilterName = "",
}: Props) => {
  const form = useFormContext<MatchFilterState>()
  const { setValue } = form;
  const reset = useFormReset(form);

  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;

  const open = isControlled ? controlledOpen! : uncontrolledOpen;
  const setopen = isControlled ? (onOpenChange ?? (() => {})) : setUncontrolledOpen;

  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [filterName, setFilterName] = useState("");

  const { trpc } = useTRPC();
  const { user } = useAuth();

  const user_id = user!.id;

  const queryClient = useQueryClient();

  const createFilter = useCreateFilter(form, "MATCHES", user_id, filterName, () => {
    setNameDialogOpen(false);
    setFilterName("");
    queryClient.invalidateQueries({ queryKey: ["filter", user?.id, "MATCHES"] });
    toast.success("Filter succesvol opgeslagen");
  });

  const isEdit = mode === "edit";

  React.useEffect(() => {
    if (isEdit && open && initialFilterName) {
      setFilterName(initialFilterName);
    }
  }, [isEdit, open, initialFilterName]);

  return (
    <>
      <AlertDialog open={open} onOpenChange={setopen}>
        {!isControlled && (
          <AlertDialogTrigger asChild>
            <Button variant="bright" shape="pill" size="2xl">
              <Icons.Search className="w-4 h-4" />
              Nieuwe zoekfilter
            </Button>
          </AlertDialogTrigger>
        )}

        <AlertDialogContent className="max-h-[95dvh] overflow-auto flex flex-col justify-between p-0 gap-0">
          <AlertDialogHeader className="p-4 flex items-center justify-center border-b relative">
            <AlertDialogTitle>{isEdit ? "Filter bewerken" : "Filters"}</AlertDialogTitle>
            <button onClick={() => setopen(false)} className="absolute right-4 top-1/2 -translate-y-1/2">
              <Icons.X className="w-4 h-4" />
            </button>
          </AlertDialogHeader>

          <div className="h-full overflow-auto px-4 space-y-4">
            <Accordion type="multiple" defaultValue={["general-search"]}>
              <GeneralSearch />
              <CompetitionClub />
              <DateRange />
            </Accordion>
          </div>

          <div className="flex justify-between w-full p-4 border-t">
            <Button
              onClick={() => {
                setValue("search", "");
                setValue("competition.competition_id", "");
                setValue("competition.club_id", "");
                setValue("daterange.date_from", "");
                setValue("daterange.date_to", "");
                reset();
                setValue("page", 1);
                onSearch?.();
              }}
              variant="ghost"
            >
              Alles wissen
            </Button>

            <Button
              onClick={() => {
                  setNameDialogOpen(true);
              }}
            >
              Filter opslaan
            </Button>

            {!isEdit && (
              <Button
                onClick={() => {
                  form.setValue("page", 1);
                  onSearch?.();
                  setopen(false);
                }}
              >
                Matches weergeven
              </Button>
            )}
          </div>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={nameDialogOpen} onOpenChange={setNameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEdit ? "Filter bijwerken" : "Filter opslaan"}</DialogTitle> 
          </DialogHeader>
          <Input
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            placeholder="Voer een naam in voor de filter"
          />
          <DialogFooter>
            <Button onClick={() => {
              const values = form.getValues();
              onUpdate?.({ values, name: filterName }); 
              if (isEdit) {
                setopen(false); 
              } else {
                createFilter.mutate(); 
              }
              setNameDialogOpen(false);
              setFilterName("");
            }}>
              {isEdit ? "Bijwerken" : "Opslaan"} 
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const GeneralSearch = () => {
  const { control } = useFormContext<MatchFilterState>()

  return (
    <AccordionItem value="general-search" className="border-b">
      <AccordionTrigger>
        <p className="text-base font-medium">Algemeen zoekveld</p>
      </AccordionTrigger>
      <AccordionContent>
        <Controller
          control={control}
          name="search"
          render={({ field }) => (
            <Input
              {...field}
              shape="pill"
              className="h-10"
              placeholder="Zoek naar clubs, ID's..."
              startContent={<Icons.Search className="w-4 h-4" />}
            />
          )}
        />
      </AccordionContent>
    </AccordionItem>
  )
}

const CompetitionClub = () => {
  const { control, watch, setValue } = useFormContext<MatchFilterState>()
  const competitionId = watch('competition.competition_id')

  return (
    <AccordionItem value="competition-club" className="border-b">
      <AccordionTrigger>
        <p className="text-base font-medium">Competitie/club</p>
      </AccordionTrigger>
      <AccordionContent>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Controller
              control={control}
              name="competition.competition_id"
              render={({ field }) => (
                <SelectCompetition
                  value={watch("competition.competition_id")}
                  label={watch("competition.competition_name")}
                  onValueChange={(id, lbl) => {
                    setValue("competition.competition_id", id ?? "");
                    setValue("competition.competition_name", lbl ?? "");
                  }}
                />
              )}
            />
          </div>
          <div>
            <Controller
              control={control}
              name="competition.club_id"
              render={({ field }) => (
                <SelectClub
                  value={watch("competition.club_id")}
                  label={watch("competition.club_name")}
                  onValueChange={(id, lbl) => {
                    setValue("competition.club_id", id ?? "");
                    setValue("competition.club_name", lbl ?? "");
                  }}
                  searchOptions={{
                    competition: {
                      competition_id: competitionId ?? undefined,
                    },
                  }}
                />
              )}
            />
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

const DateRange = () => {
  const { control, setValue } = useFormContext<MatchFilterState>()

  return (
    <AccordionItem value="daterange" className="border-b">
      <AccordionTrigger>
        <p className="text-base font-medium">Datum van-tot</p>
      </AccordionTrigger>
      <AccordionContent>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Controller
              control={control}
              name="daterange.date_from"
              render={({ field }) => (
                <DateInput
                  date={field.value ? convertToDate(field.value) : undefined}
                  onDateChange={(d) => {
                    const iso = d?.toISOString();
                    field.onChange(iso);
                    setValue('daterange.date_from_display', d ? d.toLocaleDateString('nl-BE', { year: 'numeric', month: 'long', day: 'numeric' }) : ''); 
                  }}                />
              )}
            />
          </div>
          <div>
            <Controller
              control={control}
              name="daterange.date_to"
              render={({ field }) => (
                <DateInput
                  date={field.value ? convertToDate(field.value) : undefined}
                  onDateChange={(d) => {
                    const iso = d?.toISOString();
                    field.onChange(iso);
                    setValue('daterange.date_to_display', d ? d.toLocaleDateString('nl-BE', { year: 'numeric', month: 'long', day: 'numeric' }) : ''); 
                  }}
                />
              )}
            />
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
